import firebase from 'firebase/app'
import 'firebase/functions'
import 'firebase/firestore'
import { reactLocalStorage } from 'reactjs-localstorage'

export const WORKSPACE_CHANGED_EVENT = 'school-ctx-changed'
export const LAST_WORKSPACE_STORAGE_PREFIX = 'edusaf:last-workspace:'

const PERMISSION_KEYS = [
  'people',
  'teachers',
  'students',
  'user',
  'attendance',
  'assessment',
  'scheme_of_work',
  'lessonplan',
  'admission',
  'payment',
  'expenses',
  'message',
  'timetable',
  'card',
  'school',
  'graduated',
  'biometrics',
  'subscription',
]

const SCHOOL_CONTEXT_KEYS = [
  ...PERMISSION_KEYS,
  'schoolPermission',
  'admission-duty',
  'admission-access-mode',
  'selectedClasses',
  'teacherRoles',
  'form_master',
  'form_master2',
  'current-school',
  'school-name',
  'school-email',
  'school-phone',
  'address',
  'heading',
  'photo',
  'term',
  'session',
  'start_date',
  'end_date',
  'next_date',
  'position',
  'hide-attendance',
  'activated',
  'total_students',
  'doc_id',
  'name',
  'email',
  'phone',
  'role',
  'account_type',
  'state',
  'lga',
  'schoolcode',
  'schooltype',
  'sms_id',
  'dashboard',
  'platform-admin',
  'platform-console',
  'developer-subscription-bypass',
  'developer-context-mode',
  'developer-context-school',
  'developer-context-at',
  'workspace-school-id',
  'workspace-profile-id',
  'workspace-persona',
  'workspace-label',
  'teacher-active-tab',
]

const asText = (value) => String(value ?? '').trim()
const lower = (value) => asText(value).toLowerCase()
const workspaceMetadataCache = new Map()

function schoolCodeFromSources(
  schoolData = {},
  settingsData = {},
  legacySchoolData = {}
) {
  const details = settingsData.schoolDetails || {}
  return asText(
    schoolData.schoolcode ||
      schoolData.schoolCode ||
      schoolData.school_code ||
      schoolData['School Code'] ||
      schoolData.code ||
      legacySchoolData.schoolcode ||
      legacySchoolData.schoolCode ||
      legacySchoolData.school_code ||
      legacySchoolData['School Code'] ||
      legacySchoolData.code ||
      details.schoolcode ||
      details.schoolCode ||
      details.school_code ||
      details['School Code'] ||
      settingsData.schoolcode ||
      settingsData.schoolCode ||
      settingsData.school_code ||
      settingsData['School Code'] ||
      settingsData.code
  ).toUpperCase()
}

function schoolLogoFromSources(
  schoolData = {},
  settingsData = {},
  legacySchoolData = {},
  profileData = {}
) {
  const details = settingsData.schoolDetails || {}
  return asText(
    settingsData.branding?.logo ||
      details.logo ||
      details.schoolLogo ||
      schoolData.branding?.logo ||
      schoolData.logo ||
      schoolData.schoolLogo ||
      schoolData.photo ||
      schoolData.image ||
      legacySchoolData.branding?.logo ||
      legacySchoolData.logo ||
      legacySchoolData.schoolLogo ||
      legacySchoolData.photo ||
      legacySchoolData.image ||
      profileData.schoolLogo ||
      profileData.logo ||
      profileData.photo ||
      profileData.image
  )
}

function profileCollectionForPersona(persona) {
  if (persona === 'owner') return 'persons'
  if (persona === 'sub_admin') return 'user'
  if (persona === 'teacher') return 'teachers'
  return ''
}

async function loadWorkspaceMetadata(workspace = {}) {
  const cleanSchoolId = asText(workspace.schoolId)
  if (!cleanSchoolId) return { schoolCode: '', logo: '' }
  if (workspaceMetadataCache.has(cleanSchoolId)) {
    return workspaceMetadataCache.get(cleanSchoolId)
  }

  try {
    const db = firebase.firestore()
    const profiles = Array.isArray(workspace.profiles) ? workspace.profiles : []
    const profile =
      profiles.find((item) => item.persona === workspace.defaultPersona) ||
      profiles[0] ||
      null
    const profileCollection = profileCollectionForPersona(profile?.persona)
    const [schoolSnap, settingsSnap, legacySchoolSnap, profileSnap] = await Promise.all([
      db.collection('schools').doc(cleanSchoolId).get(),
      db.collection('settings').doc(cleanSchoolId).get(),
      db
        .collection('school')
        .where('schoolId', '==', cleanSchoolId)
        .limit(1)
        .get(),
      profileCollection && profile?.profileId
        ? db.collection(profileCollection).doc(profile.profileId).get()
        : Promise.resolve(null),
    ])
    const schoolData = schoolSnap.exists ? schoolSnap.data() || {} : {}
    const settingsData = settingsSnap.exists ? settingsSnap.data() || {} : {}
    const legacySchoolData = !legacySchoolSnap.empty
      ? legacySchoolSnap.docs[0].data() || {}
      : {}
    const profileData = profileSnap?.exists ? profileSnap.data() || {} : {}
    const metadata = {
      schoolCode: schoolCodeFromSources(
        schoolData,
        settingsData,
        legacySchoolData
      ),
      logo: schoolLogoFromSources(
        schoolData,
        settingsData,
        legacySchoolData,
        profileData
      ),
    }
    workspaceMetadataCache.set(cleanSchoolId, metadata)
    return metadata
  } catch (error) {
    console.warn(
      `[workspaceService] unable to resolve school metadata for ${cleanSchoolId}:`,
      error
    )
    return { schoolCode: '', logo: '' }
  }
}

export function lastWorkspaceStorageKey(uid) {
  const cleanUid = asText(uid)
  return cleanUid ? `${LAST_WORKSPACE_STORAGE_PREFIX}${cleanUid}` : ''
}

export function rememberedWorkspaceId(uid) {
  const key = lastWorkspaceStorageKey(uid)
  return asText(
    (key && reactLocalStorage.get(key)) ||
      reactLocalStorage.get('last-workspace-school-id')
  )
}

const isTruthy = (value) => {
  if (value === true || value === 1) return true
  return ['true', '1', 'yes', 'on', 'enabled', 'active'].includes(lower(value))
}

function getFunctions() {
  if (typeof firebase.functions === 'function') return firebase.functions()
  try {
    const app = firebase.app?.()
    if (app && typeof app.functions === 'function') return app.functions()
  } catch (_) {}
  return null
}

async function callWorkspaceFunction(name, payload = {}) {
  const functions = getFunctions()
  if (!functions || typeof functions.httpsCallable !== 'function') {
    const error = new Error('Workspace switching is not available yet. Please reload and try again.')
    error.code = 'functions/unavailable'
    throw error
  }
  const response = await functions.httpsCallable(name)(payload)
  return response?.data || {}
}

export async function listMyWorkspaces({ hydrateSchoolMetadata = false } = {}) {
  const result = await callWorkspaceFunction('listMyWorkspaces')
  const workspaces = Array.isArray(result.workspaces) ? result.workspaces : []
  if (!hydrateSchoolMetadata) return workspaces

  return Promise.all(
    workspaces.map(async (workspace) => {
      if (asText(workspace.schoolCode) && asText(workspace.logo)) return workspace
      const metadata = await loadWorkspaceMetadata(workspace)
      return {
        ...workspace,
        schoolCode: asText(workspace.schoolCode) || metadata.schoolCode,
        logo: asText(workspace.logo) || metadata.logo,
      }
    })
  )
}

export function workspaceDisplayName(workspace = {}) {
  return (
    asText(workspace.workspaceLabel) ||
    asText(workspace.displayName) ||
    asText(workspace.schoolName) ||
    'Unnamed school'
  )
}

export function chooseInitialWorkspace(workspaces = [], rememberedSchoolId = '') {
  const available = (Array.isArray(workspaces) ? workspaces : []).filter(
    (workspace) => workspace?.available
  )
  if (!available.length) return null

  const remembered = available.find(
    (workspace) => workspace.schoolId === asText(rememberedSchoolId)
  )
  if (remembered) return remembered

  const preferred = available.find((workspace) => workspace.preferred)
  if (preferred) return preferred

  return [...available].sort((left, right) => {
    const byName = workspaceDisplayName(left).localeCompare(
      workspaceDisplayName(right)
    )
    if (byName) return byName
    return asText(left.schoolId).localeCompare(asText(right.schoolId))
  })[0]
}

export async function updateMyWorkspaceLabel({ schoolId, label } = {}) {
  try {
    const result = await callWorkspaceFunction('updateMyWorkspaceLabel', {
      schoolId,
      label: asText(label),
    })
    return {
      schoolId: asText(result.schoolId || schoolId),
      workspaceLabel: asText(result.workspaceLabel),
    }
  } catch (error) {
    const code = lower(error?.code)
    const message = lower(error?.message)
    if (
      code.includes('internal') ||
      code.includes('not-found') ||
      code.includes('unavailable') ||
      message === 'internal'
    ) {
      const unavailable = new Error(
        'Workspace labels are not available on the server yet. Deploy the updated workspace functions, then try again.'
      )
      unavailable.code = code || 'functions/unavailable'
      throw unavailable
    }
    throw error
  }
}

export async function resolveMyWorkspace({ schoolId, persona, profileId } = {}) {
  const result = await callWorkspaceFunction('resolveMyWorkspace', {
    schoolId,
    persona: persona || undefined,
    profileId: profileId || undefined,
  })
  if (!result.workspace) throw new Error('The selected workspace could not be loaded.')
  return result.workspace
}

export function clearSchoolWorkspaceContext() {
  SCHOOL_CONTEXT_KEYS.forEach((key) => reactLocalStorage.remove(key))
}

function persistPermissions(data = {}) {
  const roles = Array.isArray(data.roles)
    ? data.roles.map((role) => lower(role)).filter(Boolean)
    : []
  const allowed = new Set(roles)

  PERMISSION_KEYS.forEach((key) => {
    if (isTruthy(data[key])) allowed.add(key)
  })
  if (isTruthy(data.schoolPermission)) allowed.add('school')
  if (allowed.has('people')) {
    allowed.add('teachers')
    allowed.add('students')
    allowed.add('user')
  }
  allowed.forEach((key) => {
    if (PERMISSION_KEYS.includes(key)) reactLocalStorage.set(key, key)
  })
  if (allowed.has('school')) reactLocalStorage.set('schoolPermission', 'school')
}

export function routeForWorkspace(workspace = {}) {
  const persona = asText(workspace?.profile?.persona)
  if (persona !== 'teacher') return '/landing'

  const profile = workspace?.profile?.data || {}
  if (
    isTruthy(profile.admissionDutyAccess) ||
    isTruthy(profile.admissionScopedAccess)
  ) {
    return '/landing/admission'
  }
  const schoolType = lower(workspace?.school?.type)
  return schoolType.includes('college') || schoolType.includes('tertiary')
    ? '/lecturer'
    : '/dashboard'
}

export function applyWorkspaceContext(workspace = {}) {
  const school = workspace.school || {}
  const term = workspace.term || {}
  const profile = workspace.profile || {}
  const data = profile.data || {}
  const schoolId = asText(school.id)
  if (!schoolId || !profile.id || !profile.persona) {
    throw new Error('The selected workspace is incomplete.')
  }

  clearSchoolWorkspaceContext()

  const accountType =
    profile.persona === 'teacher'
      ? 'teacher'
      : profile.persona === 'sub_admin'
      ? 'user'
      : 'person'
  const defaultRole =
    profile.persona === 'teacher' ? 'Teaching' : 'Admin'
  const name = asText(data.name || data.displayName || data.teacherName)
  const branding = school.branding || {}

  reactLocalStorage.set('current-school', schoolId)
  reactLocalStorage.set('workspace-school-id', schoolId)
  reactLocalStorage.set('workspace-profile-id', profile.id)
  reactLocalStorage.set('workspace-persona', profile.persona)
  reactLocalStorage.set('workspace-label', asText(school.workspaceLabel))
  reactLocalStorage.set('last-workspace-school-id', schoolId)
  const identityKey = lastWorkspaceStorageKey(firebase.auth?.().currentUser?.uid)
  if (identityKey) reactLocalStorage.set(identityKey, schoolId)
  reactLocalStorage.set('doc_id', profile.id)
  reactLocalStorage.set('account_type', accountType)
  reactLocalStorage.set('role', asText(data.role) || defaultRole)
  reactLocalStorage.set('name', name)
  reactLocalStorage.set('email', asText(data.email))
  reactLocalStorage.set('phone', asText(data.phone || data.phoneNumber))
  reactLocalStorage.set('school-name', asText(school.name || school.schoolname) || 'Your School')
  reactLocalStorage.set('school-email', asText(school.email))
  reactLocalStorage.set('school-phone', asText(school.phone))
  reactLocalStorage.set('address', asText(school.address))
  reactLocalStorage.set('photo', asText(data.photo || data.image || branding.logo))
  reactLocalStorage.set('heading', asText(branding.favicon || branding.letterhead))
  reactLocalStorage.set('schooltype', asText(school.type || data.schooltype || data.type) || 'school')
  reactLocalStorage.set('schoolcode', asText(data.schoolcode || school.schoolcode) || 'EDU')
  reactLocalStorage.set('sms_id', asText(school.sms_id || data.sms_id) || 'EDUSAF')
  reactLocalStorage.set('state', asText(data.state || school.state))
  reactLocalStorage.set('lga', asText(data.lga || school.lga))
  reactLocalStorage.set('platform-admin', 'false')
  reactLocalStorage.set('developer-subscription-bypass', 'false')

  if (profile.persona === 'sub_admin') {
    reactLocalStorage.set('dashboard', 'dashboard')
    persistPermissions(data)
  }

  if (profile.persona === 'teacher') {
    const teacherRoles = data.teacherRoles || data.teacher_roles || data.roles || []
    if (Array.isArray(teacherRoles) && teacherRoles.length) {
      reactLocalStorage.set('teacherRoles', JSON.stringify(teacherRoles))
    }
    if (data.form_master) reactLocalStorage.set('form_master', data.form_master)
    if (data.form_master2) reactLocalStorage.set('form_master2', data.form_master2)
    if (
      isTruthy(data.admissionDutyAccess) ||
      isTruthy(data.admissionScopedAccess)
    ) {
      reactLocalStorage.set('admission-duty', 'true')
      reactLocalStorage.set(
        'admission-access-mode',
        asText(data.admissionAccessMode) || 'scoped'
      )
    }
  }

  const termMappings = {
    term: 'term',
    session: 'session',
    start: 'start_date',
    end: 'end_date',
    next: 'next_date',
    position: 'position',
    hide_attendance: 'hide-attendance',
  }
  Object.entries(termMappings).forEach(([source, destination]) => {
    if (term[source] !== undefined && term[source] !== null) {
      reactLocalStorage.set(destination, String(term[source]))
    }
  })

  try {
    window.dispatchEvent(new Event(WORKSPACE_CHANGED_EVENT))
  } catch (_) {}
  return routeForWorkspace(workspace)
}

export async function activateWorkspace(selection, { navigate = true } = {}) {
  const workspace = await resolveMyWorkspace(selection)
  const route = applyWorkspaceContext(workspace)
  if (navigate && typeof window !== 'undefined') {
    // Most legacy screens read localStorage only during mount. A clean reload
    // makes the context swap atomic until those screens move to a provider.
    window.location.assign(route)
  }
  return { workspace, route }
}

export function workspaceErrorMessage(error) {
  const message = asText(error?.message).replace(/^Firebase:\s*/i, '')
  if (message) return message
  return 'We could not switch schools. Your current workspace was not changed.'
}
